/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { EarningsEdgeResponse } from '../models/EarningsEdgeResponse';
import type { EarningsScanRequest } from '../models/EarningsScanRequest';
import type { EarningsScanResponse } from '../models/EarningsScanResponse';
import type { EarningsSetupResponse } from '../models/EarningsSetupResponse';
import type { HistoricalMoveResponse } from '../models/HistoricalMoveResponse';
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class EarningsAlphaService {
    /**
     * Get Earnings Edge
     * Get earnings edge analysis for a single ticker.
     *
     * Compares the current implied move (from ATM straddle) to the average
     * historical move to identify if options are over/underpriced around earnings.
     *
     * - **Positive edge**: Historical moves > implied move -> Options may be underpriced -> BUY premium
     * - **Negative edge**: Historical moves < implied move -> Options may be overpriced -> SELL premium
     *
     * Edge threshold of +/- 15% is used for recommendations.
     * @returns EarningsEdgeResponse Successful Response
     * @throws ApiError
     */
    public static getEarningsEdgeApiV1EarningsAlphaTickerGet({
        ticker,
        numQuarters = 8,
    }: {
        ticker: string,
        /**
         * Historical quarters to analyze
         */
        numQuarters?: number,
    }): CancelablePromise<EarningsEdgeResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/earnings-alpha/{ticker}',
            path: {
                'ticker': ticker,
            },
            query: {
                'num_quarters': numQuarters,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Scan Earnings Opportunities
     * Scan multiple tickers for earnings mispricing opportunities.
     *
     * Filters for:
     * - Tickers with upcoming earnings within the horizon
     * - Minimum absolute edge percentage
     * - Minimum data quality (historical quarters available)
     *
     * Results are sorted by absolute edge (highest first).
     *
     * This endpoint can be slow for large ticker lists as it queries
     * ORATS for each ticker. Consider limiting to 20-30 tickers.
     * @returns EarningsScanResponse Successful Response
     * @throws ApiError
     */
    public static scanEarningsOpportunitiesApiV1EarningsAlphaScanPost({
        requestBody,
    }: {
        requestBody: EarningsScanRequest,
    }): CancelablePromise<EarningsScanResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/earnings-alpha/scan',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Historical Earnings
     * Get historical earnings moves for a ticker.
     *
     * Returns a list of past earnings moves with:
     * - Date of each earnings announcement
     * - Absolute percentage move
     * - Direction (up/down)
     * - Statistics (average, std dev, counts)
     *
     * Useful for understanding the historical volatility pattern
     * around earnings for a specific stock.
     * @returns HistoricalMoveResponse Successful Response
     * @throws ApiError
     */
    public static getHistoricalEarningsApiV1EarningsAlphaTickerHistoricalGet({
        ticker,
        numQuarters = 12,
    }: {
        ticker: string,
        /**
         * Number of quarters to retrieve
         */
        numQuarters?: number,
    }): CancelablePromise<HistoricalMoveResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/earnings-alpha/{ticker}/historical',
            path: {
                'ticker': ticker,
            },
            query: {
                'num_quarters': numQuarters,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Earnings Setup
     * Get comprehensive earnings setup analysis for a ticker.
     *
     * This is the main endpoint for pre-earnings analysis. It combines:
     *
     * **Layer 1 - Volatility Edge:**
     * - Implied move from ATM straddle
     * - Historical average earnings move
     * - Edge percentage (over/underpriced options)
     *
     * **Layer 2 - IV Crush Analysis:**
     * - Breakeven price after IV crush
     * - Real edge after accounting for crush
     * - Trade viability assessment
     *
     * **Layer 3 - Valuation Signals:**
     * - Forward P/E and historical percentile
     * - Valuation bar to clear (low/normal/high/perfection)
     * - Implied growth rate from reverse DCF
     *
     * **Layer 4 - Positioning:**
     * - Pre-earnings run-up detection
     * - Priced-for-perfection warning
     *
     * **Layer 5 - Estimates (if API configured):**
     * - Consensus EPS
     * - Whisper EPS (what traders really expect)
     *
     * **Synthesis:**
     * - Aggregated risk flags (red/yellow/green)
     * - Position size modifier (0.0 - 1.5)
     * - Overall recommendation
     *
     * Returns a comprehensive EarningsSetup object that answers:
     * "Can I actually make money on this earnings trade?"
     * @returns EarningsSetupResponse Successful Response
     * @throws ApiError
     */
    public static getEarningsSetupApiV1EarningsAlphaTickerSetupGet({
        ticker,
    }: {
        ticker: string,
    }): CancelablePromise<EarningsSetupResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/earnings-alpha/{ticker}/setup',
            path: {
                'ticker': ticker,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Capture Backtest Snapshot
     * Capture a snapshot of current earnings signals for backtest validation.
     *
     * This endpoint is called when we make an earnings recommendation.
     * Later, after earnings, we validate with the actual results.
     * @returns any Successful Response
     * @throws ApiError
     */
    public static captureBacktestSnapshotApiV1EarningsAlphaBacktestSnapshotTickerPost({
        ticker,
    }: {
        ticker: string,
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/earnings-alpha/backtest/snapshot/{ticker}',
            path: {
                'ticker': ticker,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Backtest Stats
     * Get accuracy statistics for validated backtest signals.
     *
     * Returns win rates for SELL_PREMIUM and BUY_PREMIUM recommendations
     * after comparing our signals to actual earnings moves.
     * @returns any Successful Response
     * @throws ApiError
     */
    public static getBacktestStatsApiV1EarningsAlphaBacktestStatsGet({
        minSamples = 10,
        recommendation,
    }: {
        minSamples?: number,
        /**
         * Filter by recommendation type
         */
        recommendation?: (string | null),
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/earnings-alpha/backtest/stats',
            query: {
                'min_samples': minSamples,
                'recommendation': recommendation,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Contagion Signals
     * Get active contagion signals for a target ticker.
     *
     * Returns signals from related companies that have already reported earnings.
     * For example, if MSFT beat on Azure, GOOG would have a bullish signal.
     * @returns any Successful Response
     * @throws ApiError
     */
    public static getContagionSignalsApiV1EarningsAlphaContagionTickerGet({
        ticker,
    }: {
        ticker: string,
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/earnings-alpha/contagion/{ticker}',
            path: {
                'ticker': ticker,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Generate Contagion Signals
     * Generate contagion signals from an earnings result.
     *
     * Call this after a company reports earnings to propagate signals
     * to related tickers. For example:
     * - MSFT beats on Azure → bullish signals for GOOG, AMZN cloud
     * - NVDA misses on Data Center → bearish signals for AMD, INTC
     * @returns any Successful Response
     * @throws ApiError
     */
    public static generateContagionSignalsApiV1EarningsAlphaContagionGeneratePost({
        sourceTicker,
        result,
        sourceSegment,
    }: {
        /**
         * Ticker that reported earnings
         */
        sourceTicker: string,
        /**
         * beat, miss, or inline
         */
        result: string,
        /**
         * Specific segment (e.g., Azure)
         */
        sourceSegment?: (string | null),
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/earnings-alpha/contagion/generate',
            query: {
                'source_ticker': sourceTicker,
                'source_segment': sourceSegment,
                'result': result,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Heuristics Summary
     * Get win rate heuristics summary.
     *
     * Returns aggregated win rates by:
     * - IV rank bucket (0-25, 25-50, 50-75, 75-100)
     * - Sector (Technology, Healthcare, etc.)
     *
     * Includes insights like "High IV trades are profitable: 68% win rate"
     * @returns any Successful Response
     * @throws ApiError
     */
    public static getHeuristicsSummaryApiV1EarningsAlphaHeuristicsSummaryGet(): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/earnings-alpha/heuristics/summary',
        });
    }
    /**
     * Get Segment Templates
     * Get pre-configured segment analysis templates for a ticker.
     *
     * Returns segment weights, key metrics, and peer tickers for
     * mega-cap stocks. Used to focus earnings analysis on the
     * most important business segments.
     *
     * For example, GOOG returns:
     * - Search (45% weight) - peers: META, SNAP, TTD
     * - YouTube (15% weight) - peers: META, NFLX, DIS
     * - Google Cloud (25% weight) - peers: MSFT, AMZN, CRM
     * - Other Bets (5% weight)
     * - AI/Gemini (10% weight) - peers: MSFT, NVDA, ORCL
     * @returns any Successful Response
     * @throws ApiError
     */
    public static getSegmentTemplatesApiV1EarningsAlphaTickerSegmentsGet({
        ticker,
    }: {
        ticker: string,
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/earnings-alpha/{ticker}/segments',
            path: {
                'ticker': ticker,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Validate Backtest Snapshot
     * Validate a backtest snapshot with actual earnings results.
     *
     * Call this after earnings are announced to record whether our
     * signal was correct. This builds the accuracy database for
     * improving future recommendations.
     *
     * Args:
     * ticker: Stock symbol
     * actual_move_pct: The actual post-earnings move (e.g., -5.2 for 5.2% down, +8.3 for 8.3% up)
     * snapshot_date: Optional specific snapshot date to validate
     *
     * Returns:
     * Validation result including signal_correct boolean
     * @returns any Successful Response
     * @throws ApiError
     */
    public static validateBacktestSnapshotApiV1EarningsAlphaBacktestValidateTickerPost({
        ticker,
        actualMovePct,
        snapshotDate,
    }: {
        ticker: string,
        /**
         * Actual post-earnings move (positive for up, negative for down)
         */
        actualMovePct: number,
        /**
         * Snapshot date to validate (YYYY-MM-DD). Uses most recent if not provided.
         */
        snapshotDate?: (string | null),
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/earnings-alpha/backtest/validate/{ticker}',
            path: {
                'ticker': ticker,
            },
            query: {
                'actual_move_pct': actualMovePct,
                'snapshot_date': snapshotDate,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
}
